import * as Sentry from '@sentry/react'
import { FirebaseError } from 'firebase/app'
import {
  type Auth,
  type AuthProvider as FirebaseAuthProvider,
  AuthErrorCodes,
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  SAMLAuthProvider,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from 'firebase/auth'
import { capitalize } from 'lodash'
import { type AuthProvider, HttpError, type UserIdentity } from 'react-admin'

import { getUserFullName } from '../components/UserFullNameField'
import { clearFormDrafts } from '../utils/formDrafts'

type Permissions = {
  currentUser: UserIdentity
  resources: unknown
}

type PermissionsProvider = {
  clear: () => void
  fetch: () => Promise<Permissions>
  get: () => Promise<Permissions>
}

type LoginParams = {
  username?: string
  password?: string
  loginType?: 'microsoft' | 'google' | 'saml' | 'password'
}

const NO_DASHBOARD_ACCESS_ERRORS = ['Invalid auth', 'Invalid auth: invalid provider']

const isNoDashboardAccess = (error: unknown) =>
  error instanceof HttpError && error.status === 401 && NO_DASHBOARD_ACCESS_ERRORS.includes(error.body?.error)

const hasFirebaseCode = (error: unknown, code: string) => error instanceof FirebaseError && error.code === code

const selectAccountParameters = (username?: string) => ({
  prompt: 'select_account',
  ...(username ? { login_hint: username } : {}),
})

const describeError = (error: unknown) => {
  if (error instanceof HttpError) {
    return `${error.message} (${error.status})`.trim()
  }
  return error instanceof Error ? error.message : String(error)
}

export default (
  firebaseClient: { auth: Auth },
  accountsProvider: { clear: () => void },
  apiClient: unknown,
  permissionsProvider: PermissionsProvider,
  publicRoutes: string[] = [],
): AuthProvider => {
  const needsAuthentication = () => !publicRoutes.includes(window.location.pathname)

  const onAuthLoad = () =>
    new Promise((resolve) => {
      onAuthStateChanged(firebaseClient.auth, resolve)
    })

  const signInWithProvider = async (provider: FirebaseAuthProvider) => {
    await signInWithPopup(firebaseClient.auth, provider)
  }

  return {
    login: async ({ username, password, loginType }: LoginParams) => {
      await signOut(firebaseClient.auth)
      let provider
      try {
        switch (loginType) {
          case 'microsoft':
            provider = new OAuthProvider('microsoft.com')
            provider.setCustomParameters(selectAccountParameters(username))
            await signInWithProvider(provider)
            break
          case 'google':
            provider = new GoogleAuthProvider()
            provider.setCustomParameters(selectAccountParameters(username))
            await signInWithProvider(provider)
            break
          case 'saml':
            await signInWithProvider(new SAMLAuthProvider('saml.default'))
            break
          default:
            await signInWithEmailAndPassword(firebaseClient.auth, username ?? '', password ?? '')
        }
        await permissionsProvider.fetch()
      } catch (error) {
        if (hasFirebaseCode(error, AuthErrorCodes.EXPIRED_POPUP_REQUEST)) {
          throw new Error('Another sign-in window was opened. Finish signing in there.')
        }
        await signOut(firebaseClient.auth).catch(() => undefined)
        if (isNoDashboardAccess(error)) {
          throw new Error('This account has no access to this dashboard. Contact your administrator.')
        }

        let message = `Login error with ${loginType ? capitalize(loginType) : 'email/password'}: ${describeError(error)}`

        if (hasFirebaseCode(error, AuthErrorCodes.INVALID_LOGIN_CREDENTIALS)) {
          message = 'Invalid credentials. Please check your email and password.'
        } else if (hasFirebaseCode(error, AuthErrorCodes.POPUP_CLOSED_BY_USER)) {
          message = 'Login popup was closed. Please try again.'
        } else if (hasFirebaseCode(error, AuthErrorCodes.NEED_CONFIRMATION)) {
          message = 'This email is already set up with another sign-in method. Sign in with the method you used before.'
        } else {
          Sentry.captureException(error)
        }
        throw new Error(message)
      }
    },
    logout: async () => {
      accountsProvider.clear()
      permissionsProvider.clear()
      clearFormDrafts()
      await signOut(firebaseClient.auth)
    },
    checkError: async (error: { status?: number }) => {
      if (error.status === 401) {
        throw new Error('Invalid credentials')
      }
    },
    checkAuth: async () => {
      await onAuthLoad()
      if (needsAuthentication() && !firebaseClient.auth.currentUser) {
        throw new Error('Please log in')
      }
    },
    resetEmail: async (email: string) => {
      return sendPasswordResetEmail(firebaseClient.auth, email, { url: window.location.origin })
    },
    getIdentity: async () => {
      const { currentUser } = await permissionsProvider.get()
      return { ...currentUser, fullName: getUserFullName(currentUser) }
    },
    getPermissions: async () => {
      return needsAuthentication() && firebaseClient.auth.currentUser ? permissionsProvider.get() : null
    },
  }
}
